import { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { PageSizer, SizerMode } from '../../components/page/PageSizer';
import { PageContainer } from '../../components/page/PageContainer';
import strings from './shifts.translations';
import { TableContainer } from '../../components/shared/table/TableContainer';
import { PageHeader } from '../../components/page/PageHeader';
import { FiClock, FiDatabase, FiMapPin } from 'react-icons/fi';
import { TableLoader } from '../../components/shared/table/TableLoader';
import { useNavigate, useParams } from 'react-router';
import { Feature } from '../../services/backstage/Feature';
import { EmployeeShiftsTable } from './EmployeeShiftsTable';
import { Button } from '../../components/shared/button/Button';
import { useLocationService } from '../../services/location/LocationService';
import { UpdateSelectedLocationsModal } from './UpdateSelectedLocationsModal';
import { MonthPicker } from '@/components/shared/date/MonthPicker';
import { dateToApiFormat } from '@/helpers/dateHelper';

export const AllShiftsPage: FC = () => {
  const { recordId } = useParams<'recordId'>();
  const { date: urlDate } = useParams<'date'>();

  const locations = useLocationService(state => state.locationList);
  const selectedShiftLocations = useLocationService(state => state.selectedShiftLocations);

  const navigate = useNavigate();

  const date = useMemo(() => {
    const date = urlDate ? new Date(urlDate) : new Date();
    date.setUTCDate(1);
    date.setUTCHours(0, 0, 0);

    return date;
  }, [urlDate]);

  const onChangeDate = useCallback(
    (date: Date) => {
      navigate(`/shifts/${dateToApiFormat(date)}`);
    },
    [navigate]
  );

  const endDate = useMemo(() => {
    const newDate = new Date(date);
    newDate.setUTCDate(1);
    newDate.setUTCMonth(newDate.getUTCMonth() + 1);
    newDate.setUTCDate(0);

    return newDate;
  }, [date]);

  const breadcrumbs = [{ name: strings.allShifts, to: '/shifts' }];

  const [showUpdateLocationModal, setShowUpdateLocationModal] = useState<boolean>(false);

  return (
    <PageContainer
      customHeader={
        <PageHeader pageTitle={strings.allShifts} breadcrumbs={breadcrumbs} icon={<FiClock />}>
          <div className="flex flex-col md:flex-row gap-2 md:gap-5">
            {locations?.length > 1 && (
              <Button
                onClick={() => setShowUpdateLocationModal(true)}
                primary
                className="flex items-center justify-center space-x-2"
              >
                <FiMapPin />
                <span>{strings.selectLocations}</span>
              </Button>
            )}
            <Feature name="reports">
              <Button
                onClick={() => navigate('/shifts/report')}
                primary
                className="flex items-center justify-center space-x-2"
              >
                <FiDatabase />
                <span>{strings.viewReport}</span>
              </Button>
            </Feature>
          </div>
        </PageHeader>
      }
    >
      <PageSizer mode={SizerMode.PAGE}>
        <TableContainer>
          <div className="md:px-6 px-4 py-3 space-y-3 border-b">
            <MonthPicker value={date} onChange={onChangeDate} showLabels={false} position="left" />
          </div>
          <Suspense fallback={<TableLoader />}>
            <EmployeeShiftsTable
              startDate={date}
              endDate={endDate}
              recordId={recordId}
              locations={selectedShiftLocations.filter(location => location.checked).map(location => location.key)}
            />
          </Suspense>
        </TableContainer>
      </PageSizer>
      <UpdateSelectedLocationsModal
        onCompleteCallback={() => {
          setShowUpdateLocationModal(false);
        }}
        open={showUpdateLocationModal}
        onClose={() => setShowUpdateLocationModal(false)}
      />
    </PageContainer>
  );
};
